import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type FormEvent,
} from "react";

export interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  disabled: boolean;
  busy: boolean;
  sources: string;
  onSources: (value: string) => void;
  allowResearch: boolean;
  onAllowResearch: (checked: boolean) => void;
  lang: "hr" | "en";
}

const COPY = {
  hr: {
    optionsLabel: "Izvori i istraživanje",
    inputLabel: "Poruka glavnom agentu",
    placeholder: "Napiši poruku glavnom agentu…",
    send: "Pošalji glavnom agentu",
    saving: "Spremanje…",
    sourcesLabel: "Izvori · najviše 10 HTTPS poveznica",
    sourcesPlaceholder: "Svaka poveznica u svoj redak",
    researchLabel:
      "Dopuštam do 3 istraživačka zahtjeva za ovu poruku, samo alatima i unutar limita odobrenih u Integrations. Bez slanja i CRM upisa.",
    help: "Poveznice su kontekst razgovora; same se stranice ne preuzimaju. Ne unosi lozinke, API ključeve ili nepotrebne osobne podatke.",
  },
  en: {
    optionsLabel: "Sources and research",
    inputLabel: "Message to the main agent",
    placeholder: "Write a message to the main agent…",
    send: "Send to the main agent",
    saving: "Saving…",
    sourcesLabel: "Sources · up to 10 HTTPS links",
    sourcesPlaceholder: "One link per line",
    researchLabel:
      "I allow up to 3 research requests for this message, only with the tools and within the limits approved in Integrations. No sending, no CRM writes.",
    help: "Links are conversation context; the pages themselves are not fetched. Do not enter passwords, API keys or unnecessary personal data.",
  },
} as const;

export function ChatComposer({
  value,
  onChange,
  onSubmit,
  disabled,
  busy,
  sources,
  onSources,
  allowResearch,
  onAllowResearch,
  lang,
}: ChatComposerProps) {
  const t = COPY[lang];
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [open, setOpen] = useState(false);
  const panelId = useId();

  // Auto-grow up to 160px.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 160) + "px";
  }, [value]);

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter" || e.shiftKey) return;
    if (e.nativeEvent.isComposing) return;
    if (disabled) return;
    if (!value.trim()) return;
    const coarse =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;
    if (coarse) return;
    e.preventDefault();
    onSubmit();
  };

  return (
    <form
      data-testid="chat-composer"
      onSubmit={submit}
      className="flex flex-col-reverse shrink-0 border-t border-border bg-background px-3 pt-2 pb-2"
    >
      <div className="flex items-end gap-2">
        <button
          type="button"
          data-testid="chat-options-toggle"
          aria-label={t.optionsLabel}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen(!open)}
          disabled={disabled}
          className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground disabled:opacity-50"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
        </button>
        <textarea
          ref={inputRef}
          data-testid="chat-input"
          aria-label={t.inputLabel}
          rows={1}
          maxLength={6000}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          placeholder={t.placeholder}
          className="min-w-0 max-h-40 flex-1 resize-none rounded-2xl border border-border bg-background px-3 py-2.5 text-sm"
        />
        <button
          type="submit"
          data-testid="chat-send"
          disabled={disabled || !value.trim()}
          className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-50"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 19V5" />
            <path d="M6 11l6-6 6 6" />
          </svg>
          <span className="sr-only">{busy ? t.saving : t.send}</span>
        </button>
      </div>

      <div
        id={panelId}
        data-testid="chat-options"
        hidden={!open}
        className="mb-2 space-y-2 rounded-xl border border-border p-3"
      >
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">
            {t.sourcesLabel}
          </span>
          <textarea
            rows={3}
            value={sources}
            onChange={(e) => onSources(e.target.value)}
            disabled={disabled}
            placeholder={t.sourcesPlaceholder}
            className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
        </label>

        <label className="flex items-start gap-2 text-xs">
          <input
            type="checkbox"
            checked={allowResearch}
            onChange={(e) => onAllowResearch(e.target.checked)}
            disabled={disabled}
            className="mt-0.5"
          />
          <span>{t.researchLabel}</span>
        </label>

        <p className="text-[11px] text-muted-foreground">{t.help}</p>
      </div>
    </form>
  );
}
